"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ManifestPlayer = void 0;
const log_node_1 = require("@video/log-node");
const mobx_1 = require("mobx");
const api_1 = require("../../api");
const adapter_1 = require("../../api/adapter");
const error_1 = require("../../api/error");
const manifest_1 = require("../../api/manifest");
const player_1 = require("../../api/player");
const feature_1 = require("../../api/player/features/feature");
const feature_2 = require("../../api/adapter/features/feature");
const errors_1 = require("../errors");
const errors_deprecated_1 = require("../errors-deprecated");
const bind_1 = require("../utils/bind");
const context_1 = require("../utils/context/context");
const event_emitter_1 = require("../utils/events/event-emitter");
const logger_1 = require("../utils/logger");
const sync_handler_1 = require("../utils/proxy/sync-handler");
const stats_1 = require("../utils/stats");
const core_1 = require("./core");
const helper_1 = require("./helper");
const media_element_supervisor_1 = require("./media-element-supervisor");
class ManifestPlayer extends event_emitter_1.ObservableEventEmitter {
    attachTo(el) {
        this.elementSupervisor = media_element_supervisor_1.MediaElementSupervisor.getSupervisor(el).attachToPlayer(this);
        // when the video element is repositioned/ rerendered, reinitialize it via player.initVideoEl to subscribe to events and play the stream if not paused
        const player = this.currentPlayer;
        if (player != null && this.elementSupervisor != null) {
            player.initVideoEl(this.elementSupervisor, true).catch((err) => this.ctx.logger.error(err));
        }
    }
    setPreferredLevel(level) {
        this.preferredLevel = level;
    }
    get noConsumerAudioAndVideo() {
        var _a;
        // need to use currentPlayer because consumerAudioEnabled and consumerVideoEnabled are not proxied
        if ((_a = this.currentPlayer) === null || _a === void 0 ? void 0 : _a.isImplements(feature_1.Feature.CONSUMER)) {
            return !this.currentPlayer.consumerAudioEnabled && !this.currentPlayer.consumerVideoEnabled;
        }
        return false;
    }
    get webrtcConnecting() {
        var _a;
        if ((_a = this.currentPlayer) === null || _a === void 0 ? void 0 : _a.isImplements(feature_1.Feature.BROADCAST)) {
            return this.currentPlayer.isConnecting;
        }
        return false;
    }
    //#endregion
    get currentPlayer() {
        return this[sync_handler_1.implementation];
    }
    set currentPlayer(player) {
        this[sync_handler_1.implementation] = player;
    }
    /**
     * Returns true when the player uses WebRTC.
     */
    get lowLatency() {
        var _a;
        if (!this.isImplements(feature_1.Feature.BITRATE_SWITCHING)) {
            return false;
        }
        return ((_a = this.currentPlayer) === null || _a === void 0 ? void 0 : _a.format) === "webrtc";
    }
    /**
     * Switches the player to WebRTC.
     * @param val
     */
    set lowLatency(val) {
        var _a, _b;
        if (!this.supportsLowLatency) {
            this.logger.warn("unable to set low latency. player does not support low latency.");
            return;
        }
        if (val) {
            if (((_a = this.currentPlayer) === null || _a === void 0 ? void 0 : _a.format) !== "webrtc") {
                this.selectWebrtcPlayer();
            }
            this.preferredLevel = api_1.SourceScoreLevel.High;
        }
        else {
            if (((_b = this.currentPlayer) === null || _b === void 0 ? void 0 : _b.format) === "webrtc") {
                this.nextPlayer();
            }
            this.preferredLevel = api_1.TranscodeScoreLevel.Highest;
        }
    }
    selectWebrtcPlayer() {
        const index = this.availablePlayers.findIndex((p) => p.id === "webrtc");
        this.selectPlayer(index);
    }
    isImplements(feature) {
        var _a;
        const impl = this[sync_handler_1.implementation];
        if (feature === feature_1.Feature.PLAYER_SELECTOR) {
            return true;
        }
        return (_a = impl === null || impl === void 0 ? void 0 : impl.isImplements(feature)) !== null && _a !== void 0 ? _a : false;
    }
    /**
     * Returns true if the player supports low latency mode.
     * This is true if the player supports WebRTC.
     */
    get supportsLowLatency() {
        return this.availablePlayers.some((p) => p.id === "webrtc");
    }
    get logger() {
        return this.ctx.logger;
    }
    constructor(ctx, provider, options) {
        var _a;
        super(false);
        this.driverFailover = false;
        // grace period for driver failover
        this.lastTimeUpdate = Date.now() + 5000;
        this.manifest = null;
        this.availablePlayers = [];
        this.providedPlayers = [];
        this.currentPlayerIndex = null;
        this.allPlayers = [];
        this.manifestLoadId = 0;
        this.blurred = false;
        this.firstLoadId = stats_1.stats.start(stats_1.STATS_EVENTS.FIRST_LOAD);
        this.ctx = ctx;
        this.options = {
            ...{ displayPoster: "preview" },
            ...options,
            ...{ driverFailoverSeconds: (_a = options.driverFailoverSeconds) !== null && _a !== void 0 ? _a : 10 },
        };
        (0, mobx_1.makeObservable)(this, {
            blurred: mobx_1.observable,
        });
        this.provider = provider;
        (0, context_1.onceCanceled)(ctx).then((reason) => this.dispose(`ManifestPlayer Context Cancelled: ${reason}`));
        // don't initialize any properties form Proxy region
        // before next line, otherwise they will be shadowed
        const self = (0, bind_1.makeBounded)(new Proxy(this, core_1.proxyHandler));
        // we replaced context so between this moment
        // and the end of the constructor we should use `self`
        // instead of `this`
        self.init();
        ctx.logger.attachObject(self);
        ctx.logger.trace("constructor()", {
            options: JSON.stringify(options),
        });
        return self;
    }
    handleTimeUpdate() {
        this.lastTimeUpdate = Date.now();
    }
    handleWebsocketReconnect() {
        this.selectWebrtcPlayer();
    }
    // noinspection JSUnusedLocalSymbols
    init() {
        this.provider.on("manifest", this.updateManifest);
        this.on("timeupdate", this.handleTimeUpdate);
        this.addInnerDisposer(() => this.off("timeupdate", this.handleTimeUpdate));
        const addOptions = {
            autoPlay: this.options.autoPlay,
            muted: this.options.muted,
            volume: this.options.volume,
        };
        this.allPlayers = this.parsePlayerSpecs(this.options.players, addOptions);
        this.validatePlayers(this.allPlayers).then(() => {
            this.provider.once("source", (body) => {
                var _a, _b, _c, _d, _e;
                this.manifest = body;
                this.emit("manifestLoadTime", stats_1.stats.stop(this.manifestLoadId, {
                    driver: ((_a = this.currentPlayer) === null || _a === void 0 ? void 0 : _a.format) || undefined,
                    abr: (_b = this.manifest) === null || _b === void 0 ? void 0 : _b.abr,
                    aor: (_c = this.manifest) === null || _c === void 0 ? void 0 : _c.aor,
                    atr: (_d = this.manifest) === null || _d === void 0 ? void 0 : _d.atr,
                    rep: (_e = this.manifest) === null || _e === void 0 ? void 0 : _e.rep,
                }));
            });
            this.manifestLoadId = stats_1.stats.start(stats_1.STATS_EVENTS.MANIFEST_LOAD);
            this.provider
                .load(this.options.displayPoster === "preview")
                .then(this.firstLoad)
                .catch((err) => this.emitError((0, errors_1.createError)(error_1.ErrorCode.PlayerLoadingFailed, "unable to start playing because of manifest loading error", {}, err)));
        });
        this.driverFailoverInterval();
        this.on("localVideoPaused", (paused) => {
            if (paused) {
                if (this.currentPlayer) {
                    this.currentPlayer.updatePosterWithLatestFrame();
                }
                else {
                    this.poster = this.selectPoster();
                }
            }
            else {
                this.driverFailoverInterval();
            }
        });
        this.on("implementation", () => this.emit("currentPlayer", this));
    }
    driverFailoverInterval() {
        if (this.options.driverFailoverSeconds != null && this.localVideoPaused !== true) {
            adapter_1.device.clearInterval(this.driverFailInterval);
            this.driverFailInterval = adapter_1.device.setInterval(() => {
                if (this.options.driverFailoverSeconds != null) {
                    if (this.localVideoPaused !== true) {
                        this.driverFailoverCheck(this.options.driverFailoverSeconds);
                    }
                    else {
                        adapter_1.device.clearInterval(this.driverFailInterval);
                    }
                }
            }, 1000);
            this.addInnerDisposer(() => adapter_1.device.clearInterval(this.driverFailInterval));
        }
    }
    driverFailoverCheck(timeout) {
        if (this.provider.currentState !== "online" ||
            this.noConsumerAudioAndVideo ||
            adapter_1.device.hidden ||
            this.webrtcConnecting) {
            /*
             * prevent failover when
             * 1. the manifest is offline or if browser page is not active
             * 2. the current player is webrtc and there is no audio or video being broadcasted
             * 3. the current player is webrtc and is currently trying to connect
             */
            this.lastTimeUpdate = Date.now();
            return;
        }
        if (Date.now() - this.lastTimeUpdate > timeout * 1000) {
            this.logger.warn(`driver failover check: no timeupdate for ${timeout} seconds`);
            // grace period for loading new player
            this.lastTimeUpdate = Date.now() + 5000;
            this.nextPlayer();
        }
    }
    firstLoad() {
        var _a;
        if (!((_a = this.elementSupervisor) === null || _a === void 0 ? void 0 : _a.hasPoster())) {
            this.poster = this.selectPoster();
        }
    }
    selectPoster() {
        var _a, _b, _c, _d, _e;
        const displayPoster = this.options.displayPoster;
        const jpeg = (_a = this.manifest) === null || _a === void 0 ? void 0 : _a.formats.jpeg;
        if (displayPoster == null || displayPoster === false || jpeg == null) {
            this.ctx.logger.debug("no posters", { displayPoster, manifest: this.manifest });
            return null;
        }
        if (displayPoster === "preview" && ((_b = this.manifest) === null || _b === void 0 ? void 0 : _b.previewImg) != null) {
            this.ctx.logger.debug("select previewImg poster");
            return (_c = this.manifest) === null || _c === void 0 ? void 0 : _c.previewImg;
        }
        let videoHeight;
        let videoWidth;
        if (typeof displayPoster === "object") {
            videoHeight = displayPoster.videoHeight;
            videoWidth = displayPoster.videoWidth;
        }
        else if (((_e = (_d = this.currentQuality) === null || _d === void 0 ? void 0 : _d.layer) === null || _e === void 0 ? void 0 : _e.appData) != null) {
            videoHeight = this.currentQuality.layer.appData.videoHeight;
            videoWidth = this.currentQuality.layer.appData.videoWidth;
        }
        const previewUrl = jpeg.encodings.find((j) => j.videoHeight === videoHeight && j.videoWidth === videoWidth);
        if (previewUrl != null) {
            this.ctx.logger.debug(`select ${videoWidth}x${videoHeight} poster`);
            return previewUrl.location;
        }
        if (jpeg.encodings.length > 0) {
            this.ctx.logger.debug("select first poster");
            return jpeg.encodings[0].location;
        }
        this.ctx.logger.debug("poster not found");
        return null;
    }
    updatePosterWithLatestFrame() {
        if (this.elementSupervisor != null && adapter_1.device.isImplements(feature_2.Feature.CREATE_SCREENSHOT)) {
            this.poster = this.elementSupervisor.createScreenshot();
        }
        else {
            this.ctx.logger.warn("Unable to set update poster. Create screenshot is not compatible with device.");
        }
    }
    parsePlayerSpecs(playerSpecList, addOptions) {
        return playerSpecList.map((p) => {
            var _a;
            if (typeof p === "string") {
                return { id: p, options: addOptions };
            }
            const options = (_a = p.options) !== null && _a !== void 0 ? _a : {};
            return { ...p, options: { ...addOptions, ...options } };
        });
    }
    async validatePlayers(optionPlayers) {
        const support = await Promise.all(optionPlayers.map((spec) => {
            return player_1.players[spec.id].isSupported(this.logger);
        }));
        const enriched = optionPlayers.map((spec, i) => {
            return { ...spec, supported: support[i] };
        });
        this.providedPlayers = enriched.filter((spec) => spec.supported);
        this.emit("availablePlayers", { players: this.providedPlayers });
        if (this.providedPlayers.length === 0) {
            this.noDrivers();
        }
    }
    updateManifest(ev) {
        var _a;
        this.emit("manifest", ev);
        const errMsg = {
            401: "MANIFEST_UNAUTHORIZED",
            403: "MANIFEST_FORBIDDEN",
            404: "MANIFEST_NOT_FOUND",
        };
        this.manifest = ev;
        if (ev.state === "forbidden") {
            // means we tried to load manifest long enough, but still got 401 or 403
            const msg = `manifest is forbidden after polling it for '${this.provider.options.unauthorizedRecoveryDuration}' ms`;
            this.emitErrorDeprecated(new errors_deprecated_1.ManifestError(msg, { loader: this.provider }));
            this.dispose(msg);
            return;
        }
        if (ev.state !== "online") {
            if (ev.code === 401 || ev.code === 403) {
                if (this.options.refetch) {
                    this.emit("info", { code: errMsg[ev.code] });
                }
                else {
                    this.emitErrorDeprecated(new errors_deprecated_1.ManifestError(`manifest error: ${errMsg[ev.code]}`, { loader: this.provider }));
                    this.dispose(errMsg[ev.code]);
                }
            }
            if (ev.code === 404 || ev.code === 403) {
                (_a = this.currentPlayer) === null || _a === void 0 ? void 0 : _a.dispose();
                this.currentPlayer = null;
                this.emit("info", { code: errMsg[ev.code] });
            }
            return;
        }
        this.availablePlayers = this.providedPlayers.filter((spec) => {
            const format = ev.formats[player_1.players[spec.id].format];
            return format != null && (0, manifest_1.isValidFormat)(format);
        });
        this.emit("availablePlayers", { players: this.availablePlayers });
        if (this.currentPlayer == null) {
            if (this.availablePlayers.length === 0) {
                this.noDrivers();
            }
            else {
                this.ctx.logger.debug("Select the first player");
                this.selectPlayer(0);
            }
        }
        // updating a list of qualities on each manifest update
        const player = this.currentPlayer;
        const format = ev.formats[player === null || player === void 0 ? void 0 : player.format];
        this.updateQualities(format);
    }
    updateQualities(format) {
        var _a, _b, _c, _d;
        // isGenericFormat() basically means it's not WebRTC
        // because WebRTC has its own quality selection logic
        const player = this.currentPlayer;
        if (player != null && (0, api_1.isGenericFormat)(format)) {
            const qualities = (_c = (0, helper_1.fetchManifestQualities)(format, (_b = (_a = format.origin) === null || _a === void 0 ? void 0 : _a.location) !== null && _b !== void 0 ? _b : null, player)) !== null && _c !== void 0 ? _c : [];
            if (!(0, helper_1.equalQualities)(qualities, (_d = this.availableQualities) !== null && _d !== void 0 ? _d : [])) {
                this.availableQualities = qualities;
            }
            this.blurred = format.substitute != null;
        }
    }
    noDrivers() {
        if (this.provider.currentState !== "online") {
            return;
        }
        this.emit("noPlayers", true);
        this.emitErrorDeprecated(new errors_deprecated_1.NoDriversError("no drivers", {}));
    }
    nextPlayer() {
        var _a, _b;
        if (this.availablePlayers.length === 1) {
            (_b = (_a = this).restart) === null || _b === void 0 ? void 0 : _b.call(_a, false);
            return;
        }
        if (this.currentPlayerIndex == null || this.currentPlayerIndex >= this.availablePlayers.length - 1) {
            this.selectPlayer(0);
        }
        else {
            this.selectPlayer(this.currentPlayerIndex + 1);
        }
    }
    selectPlayer(index) {
        var _a;
        this.suspended = true;
        this.ctx.logger.debug("selectPlayer()", { index });
        if (index >= this.availablePlayers.length) {
            this.noDrivers();
            return;
        }
        const spec = this.availablePlayers[index];
        this.emit("driver", spec.id);
        const Player = player_1.players[spec.id];
        if (Player == null) {
            this.noDrivers();
            return;
        }
        if (this.currentPlayer != null) {
            const oldPlayer = this.currentPlayer;
            this.currentPlayer = null;
            oldPlayer.removeAllListeners("error");
            oldPlayer.dispose("replacing current player");
        }
        // updating a list of qualities because new player its own qualities
        if (Player.format != null) {
            const format = (_a = this.manifest) === null || _a === void 0 ? void 0 : _a.formats[Player.format];
            this.updateQualities(format);
        }
        const player = new Player((0, logger_1.extendContext)(this.ctx, Player), this.provider, spec.options);
        this.currentPlayer = player;
        this.currentPlayerIndex = index;
        this.ctx.logger.debug("set manifest player", {
            player: this.currentPlayer,
            format: player.format,
            name: spec,
        });
        player.on("error", (err) => {
            switch (err.code) {
                case error_1.ErrorCode.EmbedSWFFailed:
                case error_1.ErrorCode.NotSupported:
                case error_1.ErrorCode.HandleHlsJsError:
                case error_1.ErrorCode.JoinCallError:
                case error_1.ErrorCode.UnableJoinCall:
                    this.ctx.logger.debug(`Select next player because ${err.code}`);
                    this.nextPlayer();
                    break;
                default:
                    if (err.critical) {
                        this.ctx.logger.debug("Select next player because the error is critical");
                        this.nextPlayer();
                    }
                    break;
            }
        });
        player.on("videoFirstPlay", () => {
            var _a, _b, _c, _d, _e;
            this.emit("initialLoadTime", stats_1.stats.stop(this.firstLoadId, {
                driver: ((_a = this.currentPlayer) === null || _a === void 0 ? void 0 : _a.format) || undefined,
                abr: (_b = this.manifest) === null || _b === void 0 ? void 0 : _b.abr,
                aor: (_c = this.manifest) === null || _c === void 0 ? void 0 : _c.aor,
                atr: (_d = this.manifest) === null || _d === void 0 ? void 0 : _d.atr,
                rep: (_e = this.manifest) === null || _e === void 0 ? void 0 : _e.rep,
            }));
        });
        if (player.isImplements(feature_1.Feature.BROADCAST)) {
            player.on("websocketReconnect", this.handleWebsocketReconnect);
            player.addInnerDisposer(() => this.off("websocketReconnect", this.handleWebsocketReconnect));
        }
        if (this.elementSupervisor != null) {
            player.initVideoEl(this.elementSupervisor, true).catch((err) => this.ctx.logger.error(err));
        }
        this.suspended = false;
    }
    handlePlayerAccessDenied(response) {
        this.dispose(response.message);
    }
    toJSON() {
        var _a, _b, _c;
        const data = (_a = this[sync_handler_1.implementation]) === null || _a === void 0 ? void 0 : _a.toJSON();
        if (!(data != null && typeof data === "object" && !Array.isArray(data) && !(0, log_node_1.isSerializableObject)(data))) {
            return {};
        }
        const aggregates = (_b = data.aggregates) !== null && _b !== void 0 ? _b : {};
        if (!(aggregates != null &&
            typeof aggregates === "object" &&
            !Array.isArray(aggregates) &&
            !(0, log_node_1.isSerializableObject)(aggregates))) {
            return data;
        }
        delete data.aggregates;
        return {
            ...data,
            options: this.options,
            playersSpecs: this.availablePlayers,
            currentPlayerIndex: this.currentPlayerIndex,
            uri: (_c = this.provider) === null || _c === void 0 ? void 0 : _c.uri,
            aggregates: {
                ...aggregates,
                ...(0, log_node_1.extractAggregates)(this.provider, "support"),
                support: this.ctx.support.hash,
            },
        };
    }
    replaceManifestUrl(url) {
        return Promise.resolve(undefined);
    }
}
exports.ManifestPlayer = ManifestPlayer;
ManifestPlayer.displayName = "ManifestPlayer";
//# sourceMappingURL=manifest.js.map